import { useState, useCallback, useEffect, useMemo } from 'react';
import { useSession } from './auth/useSession';
import { supabase } from '../lib/supabaseClient';
import { useNavigate } from 'react-router-dom';
import { clearSecureStorage } from '../lib/encryption';
import { useRowOperations } from '../hooks/useRowOperations';
import { useSupabaseData } from '../hooks/useSupabaseData';
import { staticData, mapDbRecordToRow, generateRandomCode } from '../utils/namingToolUtils';
import logger from '../lib/logger';
import HeaderSection from './HeaderSection';
import FiltersSection from './FiltersSection';
import NamingTable from './NamingTable';
import ActivityPanel from './ActivityPanel';

const NamingToolTable = () => {
  const { session } = useSession();
  const navigate = useNavigate();
  
  // Get data from Supabase
  const { clients, pages, employees, employeeMapping, namingFiles, loading, error, refetch } = useSupabaseData();
  
  // Get row operations
  const {
    addNewRow,
    duplicateRow,
    updateRow
  } = useRowOperations(employeeMapping);

  // State for local UI
  const [rows, setRows] = useState([]);
  const [localFilters, setLocalFilters] = useState({
    code: '',
    client: '',
    page: '',
    employee: '',
    createdFrom: '',
    createdTo: ''
  });
  const [localSelectedRowId, setLocalSelectedRowId] = useState(null);
  const [localClipboardData, setLocalClipboardData] = useState('');
  const [highlightedRowId, setHighlightedRowId] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
  // Grouping state (none | client | page)
  const [groupBy, setGroupBy] = useState('none');
  // Sanitize legacy nested grouping value if it persists (e.g. from hot reload)
  useEffect(() => {
    if (groupBy === 'client-page') {
      setGroupBy('client'); // fallback preference
    }
  }, [groupBy]);
  // Track collapsed groups by groupKey
  const [collapsedGroups, setCollapsedGroups] = useState(() => new Set());
  const toggleGroup = useCallback((groupKey) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupKey)) {
        next.delete(groupKey);
      } else {
        next.add(groupKey);
      }
      return next;
    });
  }, []);
  // Track whether we've applied the initial employee filter to avoid overwriting user changes
  const [initialEmployeeFilterApplied, setInitialEmployeeFilterApplied] = useState(false);

  // Wrapper setters (restore previous API names)
  const setSelectedRowId = useCallback((id) => setLocalSelectedRowId(id), []);
  const setClipboardData = useCallback((data) => setLocalClipboardData(data), []);
  const setFilters = useCallback((updater) => {
    setLocalFilters(prev => (typeof updater === 'function' ? updater(prev) : updater));
  }, []);

  // Static and dynamic dropdown data
  const dummyData = {
    ...staticData,
    clients: clients || [],
    pages: pages || [],
    employees: employees || []
  };

  // Filters accessor
  const filters = localFilters;
  const selectedRowId = localSelectedRowId;
  const clipboardData = localClipboardData;

  // Populate rows from fetched namingFiles when data loads
  useEffect(() => {
    if (namingFiles && namingFiles.length > 0) {
      const mapped = namingFiles.map(mapDbRecordToRow);
      setRows(mapped);
    } else if (namingFiles && namingFiles.length === 0 && !loading) {
      // Provide a starter row if none exist
      const ts = new Date().toISOString();
      setRows([{
        id: Date.now(),
        code: generateRandomCode(),
        device: '', client: '', page: '', section: '', usage: '', description: '', first: '', last: '', width: '', height: '', date: '', employee: '', fileName: '', driveLink: '',
        created: ts, updated: ts, history: [{ ts, field: '_init', oldValue: '', newValue: 'Row created', editor: 'System' }], isNew: true
      }]);
    }
  }, [namingFiles, loading]);

  // Auto-set Employee filter to logged-in user's full name on initial load
  useEffect(() => {
    const applyInitialEmployeeFilter = async () => {
      try {
        // Only run once, and only if user hasn't already selected an employee manually
        if (initialEmployeeFilterApplied) return;
        if (!session?.user?.email) return; // Need session email
        if (!employees || employees.length === 0) return; // Need employees list loaded
        if (filters.employee && filters.employee !== '') return; // User already set a filter

        // Attempt to find the user's full name. If users table stores email, fetch it; else infer from naming_files or employees list.
        let userFullName = null;

        // Optimistic: If there's exactly one employee that includes the email local part, try match (simple heuristic)
        const emailLocalPart = session.user.email.split('@')[0].toLowerCase();
        const heuristicMatch = employees.find(e => e.toLowerCase().includes(emailLocalPart));
        if (heuristicMatch) {
          userFullName = heuristicMatch;
        } else {
          // Fallback: query users table for this email to get full_name (requires email column existing)
          try {
            const { data, error } = await supabase
              .from('users')
              .select('full_name, email')
              .eq('email', session.user.email)
              .maybeSingle();
            if (!error && data?.full_name) {
              userFullName = data.full_name;
            }
          } catch (innerErr) {
            logger.warn?.('Failed user full_name lookup, continuing without preset', innerErr);
          }
        }

        if (userFullName && employees.includes(userFullName)) {
          setFilters(prev => ({ ...prev, employee: userFullName }));
          setInitialEmployeeFilterApplied(true);
          logger.info?.('Applied initial employee filter for user', { userFullName });
        } else {
          // If we can't determine, mark as applied to prevent repeated attempts
          setInitialEmployeeFilterApplied(true);
        }
      } catch (err) {
        logger.errorSecure?.('Error applying initial employee filter', err);
        setInitialEmployeeFilterApplied(true);
      }
    };
    applyInitialEmployeeFilter();
  }, [session, employees, filters.employee, initialEmployeeFilterApplied, setFilters]);

  // Filter logic
  const filteredRows = useMemo(() => {
    return rows.filter(row => {
      // CODE (partial, case-insensitive match)
      if (filters.code) {
        const codeNeedle = filters.code.toLowerCase();
        const rowCode = (row.code || '').toLowerCase();
        if (!rowCode.includes(codeNeedle)) return false;
      }
      if (filters.client && row.client !== filters.client) return false;
      if (filters.page && row.page !== filters.page) return false;
      if (filters.employee && row.employee !== filters.employee) return false;
      if (filters.createdFrom || filters.createdTo) {
        if (!row.date || row.date.trim() === '') {
          if (filters.createdFrom || filters.createdTo) return false;
        } else {
          const rowDate = new Date(row.date);
          if (isNaN(rowDate.getTime())) {
            if (filters.createdFrom || filters.createdTo) return false;
          } else {
            if (filters.createdFrom) {
              const fromDate = new Date(filters.createdFrom);
              fromDate.setHours(0,0,0,0);
              if (rowDate < fromDate) return false;
            }
            if (filters.createdTo) {
              const toDate = new Date(filters.createdTo);
              toDate.setHours(23,59,59,999);
              if (rowDate > toDate) return false;
            }
          }
        }
      }
      return true;
    });
  }, [rows, filters]);

  // Sorting logic
  const sortedRows = useMemo(() => {
    if (!sortConfig.key) return filteredRows;

    return [...filteredRows].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Handle created date sorting specifically
      if (sortConfig.key === 'created') {
        aValue = aValue ? new Date(aValue).getTime() : 0;
        bValue = bValue ? new Date(bValue).getTime() : 0;
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [filteredRows, sortConfig]);

  // Handle sorting
  const handleSort = useCallback((key) => {
    setSortConfig(prevConfig => {
      if (prevConfig.key === key) {
        // Toggle direction or reset if already desc
        if (prevConfig.direction === 'asc') {
          return { key, direction: 'desc' };
        } else if (prevConfig.direction === 'desc') {
          return { key: null, direction: null };
        }
      }
      return { key, direction: 'asc' };
    });
  }, []);

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  useEffect(() => { setPage(1); }, [filters, sortConfig]);
  // Pagination (applies to data rows only; group headers are inserted after slicing)
  const dataRowsForPagination = groupBy === 'none' ? sortedRows : sortedRows; // Use underlying data rows only
  const totalPages = Math.max(1, Math.ceil(dataRowsForPagination.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const pagedDataRows = dataRowsForPagination.slice(startIndex, endIndex);
  // Rebuild grouping on just the paged subset if grouping enabled
  const displayedRows = useMemo(() => {
    if (groupBy === 'none') return pagedDataRows;
    const rowsSubset = pagedDataRows;
    const output = [];
    const pushHeader = (label, level, extra = {}) => {
      const groupKey = extra.groupKey;
      const collapsed = groupKey ? collapsedGroups.has(groupKey) : false;
      output.push({ id: `__group__${level}__${label}`, __group: true, groupLabel: label, groupLevel: level, collapsed, ...extra });
    };
    if (groupBy === 'client') {
      const clientMap = rowsSubset.reduce((acc, row) => {
        const key = row.client || '(No Client)';
        (acc[key] = acc[key] || []).push(row);
        return acc;
      }, {});
      Object.keys(clientMap).sort().forEach(client => {
        pushHeader(`Client: ${client} (${clientMap[client].length})`, 1, { groupKey: client });
        if (!collapsedGroups.has(client)) {
          clientMap[client].forEach(r => output.push(r));
        }
      });
    } else if (groupBy === 'page') {
      const pageMap = rowsSubset.reduce((acc, row) => {
        const key = row.page || '(No Page)';
        (acc[key] = acc[key] || []).push(row);
        return acc;
      }, {});
      Object.keys(pageMap).sort().forEach(page => {
        pushHeader(`Page/Menu: ${page} (${pageMap[page].length})`, 1, { groupKey: page });
        if (!collapsedGroups.has(page)) {
          pageMap[page].forEach(r => output.push(r));
        }
      });
    } else {
      // Fallback for legacy value (e.g., 'client-page')
      return pagedDataRows;
    }
    return output;
  }, [groupBy, pagedDataRows, collapsedGroups]);

  // Close user menu and selected row when pressing Escape
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (selectedRowId) {
          setSelectedRowId(null);
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedRowId, setSelectedRowId]);

  // Handle global paste events
  useEffect(() => {
    const handlePaste = async (e) => {
      const target = e.target;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'SELECT')) {
        try {
          const pastedText = e.clipboardData.getData('text/plain');
          if (pastedText) {
            setClipboardData(pastedText);
            if (target.tagName === 'SELECT') {
              const options = Array.from(target.options).map(opt => opt.value);
              if (options.includes(pastedText)) {
                e.preventDefault();
                target.value = pastedText;
                target.dispatchEvent(new Event('change', { bubbles: true }));
              }
            }
          }
        } catch (err) {
          // Paste operation not supported in this browser - silently ignore
        }
      }
    };
    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [setClipboardData]);

  // Handle copy events for better tracking
  useEffect(() => {
    const handleCopy = (e) => {
      const selection = window.getSelection().toString();
      if (selection) {
        setClipboardData(selection);
      }
    };
    document.addEventListener('copy', handleCopy);
    return () => document.removeEventListener('copy', handleCopy);
  }, [setClipboardData]);

  // Clear all filters
  const clearFilters = useCallback(() => {
    try {
      setFilters({
        code: '',
        client: '',
        page: '',
        employee: '',
        createdFrom: '',
        createdTo: ''
      });
    } catch (error) {
      logger.errorSecure('Failed to clear filters', error);
    }
  }, [setFilters]);

  // Helper functions for the refactored components
  // Derive current user's full name using same heuristic used for initial filter.
  const currentUserFullName = useMemo(() => {
    if (!session?.user?.email || !employees || employees.length === 0) return null;
    const emailLocalPart = session.user.email.split('@')[0].toLowerCase();
    const heuristicMatch = employees.find(e => e.toLowerCase().includes(emailLocalPart));
    if (heuristicMatch) return heuristicMatch;
    return null; // Could extend with Supabase users table lookup if needed
  }, [session, employees]);

  const handleAddNewRow = useCallback(async () => {
    // Add the new row with employee pre-populated and do NOT select it (avoid opening activity panel)
    await addNewRow(rows, setRows, setSelectedRowId, { defaultEmployee: currentUserFullName, selectRow: false });
  }, [addNewRow, rows, setRows, setSelectedRowId, currentUserFullName]);

  const handleDuplicateRow = useCallback(async (row) => {
    await duplicateRow(row, setRows, setHighlightedRowId);
  }, [duplicateRow, setRows]);

  const handleUpdateRow = useCallback(async (id, field, value) => {
    // Clear highlight when user interacts with any field
    if (highlightedRowId) {
      setHighlightedRowId(null);
    }
    await updateRow(id, field, value, rows, setRows);
  }, [updateRow, rows, setRows, highlightedRowId]);



  const handleCopyFileName = useCallback(async (fileName) => {
    // Clear highlight when user clicks on something else
    if (highlightedRowId) {
      setHighlightedRowId(null);
    }
    const filenameToUse = fileName || 'Auto-generated filename';
    try {
      await navigator.clipboard.writeText(filenameToUse);
      setClipboardData(filenameToUse);
    } catch (err) {
      console.error('Failed to copy filename:', err);
    }
  }, [setClipboardData, highlightedRowId]);

  // Logout function
  const handleLogout = async () => {
    try {
      // Clear encrypted cache and session keys
      clearSecureStorage();

      // Sign out from Supabase
      await supabase.auth.signOut();

      // Navigate to login
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  // Get user email from session
  const userEmail = session?.user?.email || 'Unknown User';

  // Check if any filters are active
  const hasActiveFilters = Object.values(filters).some(value => value !== '');

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-sm text-gray-600 font-medium">Loading data...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    const errorMessage = error;
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center bg-white p-8 rounded-2xl shadow-lg max-w-md">
          <p className="text-sm text-red-600 font-medium mb-4">Error loading data: {errorMessage}</p>
          <div className="space-x-2">
            <button 
              onClick={refetch}
              className="px-4 py-2 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 transition-colors"
            >
              Retry Data
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    // Added pb-12 to ensure footer (pagination & page size selector) sits above the fixed horizontal scrollbar.
    // This prevents overlap where the scrollbar (fixed bottom-0) could obscure the controls.
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 pb-12">
      {/* Skip Link for Keyboard Navigation */}
      <a 
        href="#main-table" 
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 bg-blue-600 text-white px-4 py-2 rounded-lg focus:outline-none focus:ring-4 focus:ring-blue-200 z-50"
      >
        Skip to main table
      </a>
      
      {/* Live Region for Announcements */}
      <div 
        id="announcements" 
        aria-live="polite" 
        aria-atomic="true" 
        className="sr-only"
      ></div>
      
      {/* Header */}
      <HeaderSection
        userEmail={userEmail}
        onAddNewRow={handleAddNewRow}
        onLogout={handleLogout}
      />

      {/* Filters */}
      <FiltersSection
        filters={filters}
        setFilters={setFilters}
        groupBy={groupBy}
        setGroupBy={setGroupBy}
        clients={dummyData.clients}
        pages={dummyData.pages}
        employees={dummyData.employees}
        filteredRowsCount={filteredRows.length}
        totalRowsCount={rows.length}
        onClearFilters={clearFilters}
      />

      {/* Table */}
      <NamingTable
        displayedRows={displayedRows}
        staticDropdownData={dummyData}
        selectedRowId={selectedRowId}
        highlightedRowId={highlightedRowId}
        sortConfig={sortConfig}
        onSort={handleSort}
        onUpdateRow={handleUpdateRow}
        onDuplicateRow={handleDuplicateRow}
        onSetSelectedRowId={setSelectedRowId}
        onClearHighlight={() => setHighlightedRowId(null)}
        onCopyFileName={handleCopyFileName}
        onToggleGroup={toggleGroup}
      />

      {/* Footer */}
      <div className="max-w-full mx-auto px-8 mt-6 flex items-center justify-between">
        <div className="text-xs text-gray-700 font-medium flex items-center gap-3">
          <span>
            Showing {sortedRows.length} of {rows.length} record{rows.length !== 1 ? 's' : ''}
            {hasActiveFilters && <span className="text-blue-700 font-semibold"> (filtered)</span>}
            {sortConfig.key && <span className="text-purple-700 font-semibold"> (sorted by {sortConfig.key})</span>}
          </span>
          
          {/* Modern Grouping Indicator - Alternative Styles */}
          {groupBy !== 'none' && (
            <div className="flex items-center gap-2">
              <div className="h-3 w-px bg-gray-300"></div>
              
              {/* Option 1: Pill with icon (main recommendation) */}
              <div className="inline-flex items-center gap-1.5 bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-200 rounded-full px-2.5 py-1 shadow-sm">
                <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                <span className="text-[10px] font-medium text-blue-700 tracking-wide">
                  Grouped by {groupBy.replace('-', ' + ')}
                </span>
              </div>

              {/* Option 2: Minimal badge 
              <div className="inline-flex items-center gap-1 bg-blue-100 rounded-md px-2 py-0.5">
                <svg className="w-3 h-3 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z"/>
                </svg>
                <span className="text-[10px] font-medium text-blue-700">
                  {groupBy.replace('-', ' + ')}
                </span>
              </div>
              */}

              {/* Option 3: Clean text with accent
              <span className="text-[10px] font-medium text-blue-700 bg-blue-50 px-2 py-0.5 rounded border-l-2 border-blue-400">
                Grouped by {groupBy.replace('-', ' + ')}
              </span>
              */}
            </div>
          )}
        </div>
        
        <div className="flex items-center space-x-4">
          {/* Rest of your existing pagination controls */}
          <div className="flex items-center space-x-2 text-[10px] bg-white px-2 py-1.5 rounded-full border border-gray-300">
            <button onClick={() => setPage(1)} disabled={currentPage === 1} className={`px-2 py-1 rounded ${currentPage === 1 ? 'text-gray-400 cursor-not-allowed' : 'text-blue-700 hover:bg-blue-100'}`} aria-label="First page">&laquo;</button>
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className={`px-2 py-1 rounded ${currentPage === 1 ? 'text-gray-400 cursor-not-allowed' : 'text-blue-700 hover:bg-blue-100'}`} aria-label="Previous page">&lsaquo;</button>
            <span className="px-1">Page {currentPage} / {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className={`px-2 py-1 rounded ${currentPage === totalPages ? 'text-gray-400 cursor-not-allowed' : 'text-blue-700 hover:bg-blue-100'}`} aria-label="Next page">&rsaquo;</button>
            <button onClick={() => setPage(totalPages)} disabled={currentPage === totalPages} className={`px-2 py-1 rounded ${currentPage === totalPages ? 'text-gray-400 cursor-not-allowed' : 'text-blue-700 hover:bg-blue-100'}`} aria-label="Last page">&raquo;</button>
            <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} aria-label="Rows per page" className="ml-2 border border-gray-300 rounded px-1 py-0.5 text-[10px] bg-white focus:outline-none focus:ring-2 focus:ring-blue-200">
              {[50,100,250,500].map(size => (<option key={size} value={size}>{size}/pg</option>))}
            </select>
          </div>
          
          {clipboardData && (
            <div className="text-[10px] text-blue-800 bg-blue-100 px-3 py-1.5 rounded-full font-medium">
              Clipboard: "{clipboardData.length > 20 ? clipboardData.substring(0, 20) + '...' : clipboardData}"
            </div>
          )}
          
          <div className="text-[10px] text-gray-700 bg-white px-3 py-1.5 rounded-full border border-gray-300">
            <span className="text-red-700 font-bold">*</span> Required • Use Ctrl+C/V to copy/paste
          </div>
        </div>
      </div>

      {/* Activity Panel */}
      <ActivityPanel 
        selectedRowId={selectedRowId}
        rows={rows}
        onClose={() => setSelectedRowId(null)}
      />
    </div>
  );
};

export default NamingToolTable;